import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Crosshair, Play, Pause, TrendingUp, ArrowLeft, Plus, 
  Eye, Image, FileText, MapPin, DollarSign, Target,
  BarChart3, Clock, Zap, Home
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Campaigns = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);

  useEffect(() => {
    loadData();
  }, [clientId]);

  const loadData = async () => {
    try {
      const [campaignsRes, dashboardRes] = await Promise.all([
        axios.get(`${API}/campaigns/${clientId}`),
        axios.get(`${API}/dashboard/${clientId}`)
      ]);
      setCampaigns(campaignsRes.data);
      setBrands(dashboardRes.data.brands || []);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      toast.error("Erro ao carregar campanhas");
    } finally {
      setLoading(false);
    }
  };

  const createCampaign = async (brandId) => {
    setCreating(true);
    try {
      toast.info("🤖 IA criando campanha com criativo e geofencing...");
      
      const res = await axios.post(`${API}/campaigns/create/${brandId}`);
      
      if (res.data.success) {
        toast.success("✅ Campanha criada com sucesso!");
        loadData(); // Recarregar lista
      }
    } catch (error) {
      console.error("Erro ao criar campanha:", error);
      toast.error("Erro ao criar campanha");
    } finally {
      setCreating(false);
      setSelectedBrand(null);
    }
  };

  const toggleCampaignStatus = async (campaignId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'pause' : 'activate';
    try {
      await axios.post(`${API}/meta/campaigns/${campaignId}/${newStatus}?client_id=${clientId}`);
      toast.success(`Campanha ${newStatus === 'pause' ? 'pausada' : 'ativada'}!`);
      loadData();
    } catch (error) {
      toast.error("Erro ao alterar status");
    }
  };

  const optimizeCampaign = async (campaignId) => {
    try {
      toast.info("🤖 IA analisando campanha...");
      const res = await axios.post(`${API}/campaigns/${campaignId}/optimize`);
      
      if (res.data.optimization) {
        toast.success(`Sugestão: ${res.data.optimization.action}`);
      }
    } catch (error) {
      toast.error("Erro ao otimizar");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <div className="bg-[#1a1a1a] border-b border-gray-800">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/")}
                className="text-gray-400 hover:text-white"
              >
                <Home className="w-4 h-4 mr-1" />
                Início
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/dashboard/${clientId}`)}
                className="text-gray-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
            </div>
            <div className="flex items-center gap-3">
              <Crosshair className="w-6 h-6 text-red-600" />
              <h1 className="text-xl font-bold">Minhas Campanhas</h1>
            </div>
            <Badge variant="outline" className="text-gray-400">
              {campaigns.length} campanhas
            </Badge>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* Create Campaign Section */}
        {brands.length > 0 && (
          <Card className="bg-gradient-to-r from-red-900/20 to-red-600/10 border-red-600/50 mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5" />
                Criar Nova Campanha
              </CardTitle>
              <CardDescription>Selecione uma marca para criar campanha com IA</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {brands.map((brand) => (
                  <div
                    key={brand.id}
                    className={`bg-[#252525] rounded-lg p-4 cursor-pointer border-2 transition-all ${
                      selectedBrand === brand.id 
                        ? 'border-red-600' 
                        : 'border-transparent hover:border-gray-600'
                    }`}
                    onClick={() => setSelectedBrand(brand.id)}
                  >
                    <p className="font-medium truncate">{brand.product_service?.substring(0, 40)}...</p>
                    <p className="text-sm text-gray-400">{brand.city}</p>
                    {brand.brand_summary?.nicho && (
                      <Badge variant="secondary" className="mt-2 text-xs">
                        {brand.brand_summary.nicho}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
              
              {selectedBrand && (
                <Button
                  onClick={() => createCampaign(selectedBrand)}
                  disabled={creating}
                  className="w-full mt-4 bg-red-600 hover:bg-red-700"
                >
                  {creating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Criando com IA...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4 mr-2" />
                      Criar Campanha com IA
                    </>
                  )}
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Campaigns List */}
        {campaigns.length === 0 ? (
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="py-12 text-center">
              <Target className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Nenhuma campanha criada</h3>
              <p className="text-gray-400 mb-4">Selecione uma marca acima para criar sua primeira campanha</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {campaigns.map((campaign) => {
              const config = campaign.config_json || {};
              const creative = config.creative || {};
              const performance = campaign.performance_json || {};
              
              return (
                <Card key={campaign.id} className="bg-[#1a1a1a] border-gray-800 overflow-hidden">
                  <div className="flex flex-col lg:flex-row">
                    {/* Creative Preview */}
                    <div className="lg:w-1/3 bg-gradient-to-br from-gray-800 to-gray-900 p-6">
                      <div className="mb-4">
                        <Badge className={`${
                          campaign.status === 'active' ? 'bg-green-600' :
                          campaign.status === 'paused' ? 'bg-yellow-600' :
                          'bg-gray-600'
                        }`}>
                          {campaign.status === 'active' ? '🟢 Ativa' :
                           campaign.status === 'paused' ? '⏸️ Pausada' :
                           '📝 Rascunho'}
                        </Badge>
                      </div>
                      
                      {/* Ad Preview Mock */}
                      <div className="bg-white rounded-lg p-3 text-black">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                            AD
                          </div>
                          <div>
                            <p className="text-xs font-medium">Patrocinado</p>
                          </div>
                        </div>
                        
                        {campaign.has_image ? (
                          <div className="bg-gradient-to-br from-red-500 to-orange-500 h-32 rounded-lg mb-2 flex items-center justify-center">
                            <Image className="w-8 h-8 text-white" />
                          </div>
                        ) : (
                          <div className="bg-gray-200 h-32 rounded-lg mb-2 flex items-center justify-center">
                            <FileText className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                        
                        <p className="font-bold text-sm mb-1">
                          {creative.headline || 'Título do Anúncio'}
                        </p>
                        <p className="text-xs text-gray-600 line-clamp-2">
                          {creative.primary_text || 'Texto do anúncio aparecerá aqui...'}
                        </p>
                        
                        <Button size="sm" className="w-full mt-2 bg-blue-600 hover:bg-blue-700 text-xs">
                          {creative.cta || 'Saiba Mais'}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Campaign Details & Stats */}
                    <div className="lg:w-2/3 p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-bold flex items-center gap-2">
                            <span className="text-2xl">📱</span>
                            Campanha {campaign.platform?.toUpperCase() || 'META'}
                          </h3>
                          <p className="text-sm text-gray-400">
                            Criada em {new Date(campaign.created_at).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleCampaignStatus(campaign.id, campaign.status)}
                            className={`${
                              campaign.status === 'active' 
                                ? 'border-yellow-600 text-yellow-400 hover:bg-yellow-600' 
                                : 'border-green-600 text-green-400 hover:bg-green-600'
                            } hover:text-white`}
                          >
                            {campaign.status === 'active' ? (
                              <><Pause className="w-4 h-4 mr-1" /> Pausar</>
                            ) : (
                              <><Play className="w-4 h-4 mr-1" /> Ativar</>
                            )}
                          </Button>
                          
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => optimizeCampaign(campaign.id)}
                            className="border-purple-600 text-purple-400 hover:bg-purple-600 hover:text-white"
                          >
                            <TrendingUp className="w-4 h-4 mr-1" />
                            Otimizar
                          </Button>
                        </div>
                      </div>
                      
                      {/* Performance Stats */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div className="bg-[#252525] rounded-lg p-3">
                          <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
                            <Eye className="w-3 h-3" />
                            Impressões
                          </div>
                          <p className="text-xl font-bold">
                            {(performance.impressions || 0).toLocaleString()}
                          </p>
                        </div>
                        
                        <div className="bg-[#252525] rounded-lg p-3">
                          <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
                            <Target className="w-3 h-3" />
                            Cliques
                          </div>
                          <p className="text-xl font-bold">
                            {(performance.clicks || 0).toLocaleString()}
                          </p>
                        </div>
                        
                        <div className="bg-[#252525] rounded-lg p-3">
                          <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
                            <DollarSign className="w-3 h-3" />
                            Gasto
                          </div>
                          <p className="text-xl font-bold text-yellow-400">
                            R$ {(performance.spend || 0).toFixed(2)}
                          </p>
                        </div>
                        
                        <div className="bg-[#252525] rounded-lg p-3">
                          <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
                            <BarChart3 className="w-3 h-3" />
                            Conversões
                          </div>
                          <p className="text-xl font-bold text-green-400">
                            {performance.conversions || 0}
                          </p>
                        </div>
                      </div>
                      
                      {/* Additional Metrics */}
                      <div className="flex flex-wrap gap-4 text-sm">
                        <div className="flex items-center gap-1">
                          <span className="text-gray-400">CTR:</span>
                          <span className={`font-medium ${
                            (performance.ctr || 0) > 2 ? 'text-green-400' : 
                            (performance.ctr || 0) > 1 ? 'text-yellow-400' : 'text-red-400'
                          }`}>
                            {(performance.ctr || 0).toFixed(2)}%
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-gray-400">CPC:</span>
                          <span className="font-medium">R$ {(performance.cpc || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-gray-400">CPA:</span>
                          <span className="font-medium">
                            R$ {performance.conversions > 0 
                              ? ((performance.spend || 0) / performance.conversions).toFixed(2) 
                              : '0.00'}
                          </span>
                        </div>
                        {config.budget && (
                          <div className="flex items-center gap-1">
                            <span className="text-gray-400">Orçamento:</span>
                            <span className="font-medium">R$ {config.budget}/dia</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Geofencing Info */}
                      {config.geo_locations_count > 0 && (
                        <div className="mt-4 flex items-center gap-2 text-sm text-gray-400">
                          <MapPin className="w-4 h-4 text-red-500" />
                          <span>{config.geo_locations_count} locais com geofencing ativo</span>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Campaigns;
